import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, NgZone, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, defer, firstValueFrom, from, timeout } from 'rxjs';
import { environment } from '../../environments/environment';
import { ConnectionStateService } from './connection-state.service';

export const usesFootballerAuth = (environment as { AUTH_MODE?: string }).AUTH_MODE === 'footballer';
export interface FootballerAuthClientConfig { enabled: boolean; apiBaseUrl: string; loginPath: string; googleEnabled?: boolean; }
export const FOOTBALLER_AUTH_CLIENT_CONFIG = new InjectionToken<FootballerAuthClientConfig>('FootballerAuthClientConfig');

interface Tokens { accessToken: string; }
interface Identity { sub: string; email: string; email_verified: boolean; user_id?: string; }
export type SocialProvider = 'google' | 'facebook' | 'apple';
export interface LoginMethods { providers: { provider: SocialProvider; available: boolean; connected: boolean; canUnlink: boolean }[]; passwordReady: boolean; }
interface BrowserAttempt { provider: SocialProvider; verifier: string; state: string; target: string; subject?: string; expires: number; }

/** AuthService-compatible boundary. Access tokens stay in memory; refresh secrets are HttpOnly cookies. */
@Injectable({ providedIn: 'root' })
export class FootballerAuthService {
  readonly user$ = new BehaviorSubject<Identity | null>(null);
  readonly isAuthenticated$ = new BehaviorSubject(false);
  readonly isLoading$ = new BehaviorSubject(true);
  readonly error$ = new Subject<Error>();
  readonly profileReady$ = new BehaviorSubject<string | null>(null);
  readonly googleEnabled: boolean;
  private readonly http: HttpClient;
  private readonly base: string;
  private readonly loginPath: string;
  private readonly headers = new HttpHeaders({ 'X-Footballer-Client': 'web' });
  private accessToken: string | null = null;
  private accessUntil = 0;
  private refreshInFlight: Promise<string> | null = null;
  private generation = 0;
  private sessionStopped = false;
  private readonly changes = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('footballer-auth');

  constructor(backend: HttpBackend, private router: Router, private zone: NgZone, connection: ConnectionStateService,
    @Optional() @Inject(FOOTBALLER_AUTH_CLIENT_CONFIG) config?: FootballerAuthClientConfig) {
    // Bypass the app auth interceptor for authentication itself, avoiding a refresh recursion.
    this.http = new HttpClient({ handle: request => connection.track(backend.handle(request)) });
    this.base = (config?.apiBaseUrl || environment.API_BASE_URL).replace(/\/$/, '') + '/auth';
    this.loginPath = config?.loginPath || '/auth';
    this.googleEnabled = config?.googleEnabled ?? (environment as { GOOGLE_LOGIN_ENABLED?: boolean }).GOOGLE_LOGIN_ENABLED === true;
    if (!(config?.enabled ?? usesFootballerAuth)) { this.isLoading$.next(false); return; }
    if (this.changes) this.changes.onmessage = event => {
      if (event.data === 'logout' || event.data === 'session-changed') this.zone.run(() => {
        this.sessionStopped = true;
        this.clear();
      });
    };
    queueMicrotask(() => this.restore());
    window.addEventListener('online', () => {
      if (!this.sessionStopped && !this.isAuthenticated$.value) this.restore();
    });
    connection.recovered$.subscribe(() => {
      if (!this.sessionStopped && !this.isAuthenticated$.value) void this.restore();
    });
  }

  getAccessTokenSilently(): Observable<string> {
    return defer(() => from(this.token()));
  }

  loginWithRedirect(options?: any): Observable<void> {
    const target = this.safeTarget(options?.appState?.target || this.router.url);
    const mode = options?.authorizationParams?.screen_hint === 'signup' ? 'register' : 'login';
    return defer(() => from(this.router.navigate([this.loginPath], { queryParams: { mode, target } }).then(() => undefined)));
  }

  getAdminPermissions(): Observable<string[]> {
    return defer(() => from((async () => {
      const token = await this.token();
      return await firstValueFrom(this.http.get<string[]>(this.base + '/admin-permissions', {
        headers: this.headers.set('Authorization', 'Bearer ' + token)
      }).pipe(timeout(15000)));
    })()));
  }

  async beginGoogle(target: string): Promise<void> {
    if (!this.googleEnabled) throw new Error('google_login_unavailable');
    const result = await firstValueFrom(this.http.post<{authorizationUrl: string}>(this.base + '/social/google/start',
      {}, this.options()).pipe(timeout(15000)));
    const url = new URL(result.authorizationUrl);
    if (url.origin !== 'https://accounts.google.com' || url.pathname !== '/o/oauth2/v2/auth'
        || url.username || url.password || url.hash) throw new Error('invalid_provider_redirect');
    sessionStorage.setItem('footballer-social-target', this.safeTarget(target));
    window.location.assign(url.toString());
  }

  async socialProviders(): Promise<SocialProvider[]> {
    const providers = await firstValueFrom(this.http.post<string[]>(this.base + '/social/browser/providers', {}, this.options()).pipe(timeout(15000)));
    return ['google', 'facebook', 'apple'].filter(provider => providers.includes(provider)) as SocialProvider[];
  }

  async loginMethods(): Promise<LoginMethods> { return this.socialPost<LoginMethods>('methods', {}); }
  async unlinkProvider(provider: SocialProvider): Promise<LoginMethods> { return this.socialPost<LoginMethods>(provider + '/unlink', {}); }

  private async socialPost<T>(path: string, body: unknown): Promise<T> {
    const token = await this.token();
    return firstValueFrom(this.http.post<T>(this.base + '/social/browser/' + path, body, {
      ...this.options(), headers: this.headers.set('Authorization', 'Bearer ' + token)
    }).pipe(timeout(15000)));
  }

  async beginSocial(provider: SocialProvider, target: string, registration: boolean, language: string, link = false): Promise<void> {
    if (!['google', 'facebook', 'apple'].includes(provider)) throw new Error('invalid_provider');
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    const encode = (value: Uint8Array) => btoa(String.fromCharCode(...value)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    const verifier = encode(bytes);
    const codeChallenge = encode(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
    const subject = link ? this.user$.value?.sub : undefined;
    if (link && !subject) throw this.expired();
    const body = { codeChallenge, registration, language };
    const path = provider + (link ? '/link/start' : '/start');
    const result = link ? await this.socialPost<{authorizationUrl: string; state: string}>(path, body)
      : await firstValueFrom(this.http.post<{authorizationUrl: string; state: string}>(this.base + '/social/browser/' + path, body, this.options()).pipe(timeout(15000)));
    const url = new URL(result.authorizationUrl);
    const valid = provider === 'google' ? url.origin === 'https://accounts.google.com' && url.pathname === '/o/oauth2/v2/auth'
      : provider === 'apple' ? url.origin === 'https://appleid.apple.com' && url.pathname === '/auth/authorize'
      : url.origin === 'https://www.facebook.com' && /^\/v\d+\.\d+\/dialog\/oauth$/.test(url.pathname);
    if (!valid || url.username || url.password || url.hash || !/^[A-Za-z0-9_-]{43}$/.test(result.state)) throw new Error('invalid_provider_redirect');
    sessionStorage.setItem('footballer-browser-social', JSON.stringify({ provider, verifier, state: result.state,
      target: this.safeTarget(target), subject, expires: Date.now() + 300000 } satisfies BrowserAttempt));
    window.location.assign(url.toString());
  }

  consumeBrowserAttempt(state: string): BrowserAttempt {
    const raw = sessionStorage.getItem('footballer-browser-social');
    sessionStorage.removeItem('footballer-browser-social');
    let attempt: BrowserAttempt;
    try { attempt = JSON.parse(raw || 'null'); } catch (_) { throw new Error('invalid_social_state'); }
    if (!attempt || !['google', 'facebook', 'apple'].includes(attempt.provider) || attempt.state !== state
      || !/^[A-Za-z0-9_-]{43}$/.test(state) || !/^[A-Za-z0-9_-]{43}$/.test(attempt.verifier)
      || attempt.expires < Date.now()) throw new Error('invalid_social_state');
    return { ...attempt, target: this.safeTarget(attempt.target) };
  }

  async finishBrowserSocial(attempt: BrowserAttempt, receipt: string): Promise<void> {
    if (!/^[A-Za-z0-9_-]{43}$/.test(receipt) || attempt.expires < Date.now()) throw new Error('invalid_social_receipt');
    const body = { receipt, codeVerifier: attempt.verifier };
    if (attempt.subject) {
      if (this.user$.value?.sub !== attempt.subject) throw new Error('social_account_changed');
      await this.socialPost(attempt.provider + '/link/complete', body);
      return;
    }
    await this.withLock(async () => {
      this.sessionStopped = true;
      this.clear();
      const generation = this.generation;
      try {
        const tokens = await firstValueFrom(this.http.post<Tokens>(this.base + '/social/browser/' + attempt.provider + '/redeem', body, this.options()).pipe(timeout(15000)));
        if (generation !== this.generation) throw this.expired();
        this.remember(tokens);
        this.sessionStopped = false;
        await this.loadIdentity(generation);
        if (generation !== this.generation) throw this.expired();
        this.changes?.postMessage('session-changed');
      } catch (error) {
        if (generation === this.generation) {
          this.sessionStopped = true;
          this.clear();
        }
        throw error;
      }
    });
  }

  consumeSocialTarget(): string {
    const target = sessionStorage.getItem('footballer-social-target');
    sessionStorage.removeItem('footballer-social-target');
    return this.safeTarget(target);
  }

  login(email: string, password: string): Observable<void> {
    return defer(() => from(this.withLock(async () => {
      this.sessionStopped = true;
      this.clear();
      const generation = this.generation;
      try {
        const tokens = await firstValueFrom(this.http.post<Tokens>(this.base + '/login', { email, password }, this.options()).pipe(timeout(15000)));
        if (generation !== this.generation) throw this.expired();
        this.remember(tokens);
        this.sessionStopped = false;
        await this.loadIdentity(generation);
        if (generation !== this.generation) throw this.expired();
        this.changes?.postMessage('session-changed');
      } catch (error) {
        if (generation === this.generation) {
          this.sessionStopped = true;
          this.clear();
        }
        throw error;
      }
    })));
  }

  requestEmail(email: string, language: string, registration: boolean): Observable<void> {
    return this.http.post<void>(this.base + (registration ? '/register' : '/password/request'), { email, language }, this.options()).pipe(timeout(15000));
  }

  setPassword(token: string, password: string): Observable<void> {
    return defer(() => from(this.withLock(async () => {
      await firstValueFrom(this.http.post<void>(this.base + '/password/complete', { token, password }, this.options()).pipe(timeout(15000)));
      this.sessionStopped = true;
      this.clear();
      this.changes?.postMessage('logout');
    })));
  }

  logout(options?: any): Observable<void> {
    // Existing callers intentionally do not subscribe to logout (Auth0 SDK-compatible eager action).
    this.sessionStopped = true;
    this.clear();
    this.changes?.postMessage('logout');
    // Leave protected content immediately, even if cookie revocation must wait for the network.
    // '/' is the admin dashboard, not a safe signed-out destination.
    const navigation = options?.openUrl === false ? Promise.resolve() : this.router.navigateByUrl(this.loginPath);
    const revocation = this.withLock(async () => {
      await firstValueFrom(this.http.post<void>(this.base + '/logout', {}, this.options()).pipe(timeout(15000)));
    });
    const operation = Promise.all([navigation, revocation]).then(() => undefined);
    operation.catch(() => this.zone.run(() => this.error$.next(new Error('logout_failed'))));
    return from(operation);
  }

  safeTarget(value: string): string {
    if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return '/';
    const target = new URL(value, window.location.origin);
    if (target.origin !== window.location.origin || target.pathname.startsWith('/auth')
        || target.pathname === this.loginPath) return '/';
    return target.pathname + target.search + target.hash;
  }

  private async restore(): Promise<void> {
    const generation = this.generation;
    try {
      await this.token();
      await this.loadIdentity(generation);
    } catch (error) {
      if ((error as any)?.status === 401 && generation === this.generation) this.zone.run(() => this.clear());
      // A transport failure is not proof that the session expired. Retry on reconnect or explicit login.
    } finally { this.zone.run(() => this.isLoading$.next(false)); }
  }

  private token(): Promise<string> {
    if (this.sessionStopped) return Promise.reject(this.expired());
    if (this.accessToken && Date.now() < this.accessUntil) return Promise.resolve(this.accessToken);
    if (this.refreshInFlight) return this.refreshInFlight;
    const generation = this.generation;
    this.refreshInFlight = this.withLock(async () => {
      if (generation !== this.generation) throw this.expired();
      const tokens = await firstValueFrom(this.http.post<Tokens>(this.base + '/refresh', {}, this.options()).pipe(timeout(15000)));
      if (generation !== this.generation) throw this.expired();
      // Another tab may have replaced the shared cookie while this tab was suspended.
      // Never send its pending API operation as a different account behind an old profile.
      const subject = this.user$.value?.sub;
      if (subject) {
        const current = await firstValueFrom(this.http.get<Identity>(this.base + '/me', {
          headers: this.headers.set('Authorization', 'Bearer ' + tokens.accessToken)
        }).pipe(timeout(15000)));
        if (generation !== this.generation) throw this.expired();
        if (current.sub !== subject) {
          this.sessionStopped = true;
          throw this.expired();
        }
      }
      this.remember(tokens);
      return tokens.accessToken;
    }).catch(error => {
      if ((error as any)?.status === 401 && generation === this.generation) this.zone.run(() => this.clear());
      throw error;
    }).finally(() => { this.refreshInFlight = null; });
    return this.refreshInFlight;
  }

  private async loadIdentity(generation: number): Promise<void> {
    const user = await firstValueFrom(this.http.get<Identity>(this.base + '/me', {
      headers: this.headers.set('Authorization', 'Bearer ' + this.accessToken)
    }).pipe(timeout(15000)));
    if (generation !== this.generation) return;
    this.zone.run(() => {
      this.isAuthenticated$.next(true);
      this.user$.next(user);
    });
  }

  private remember(tokens: Tokens): void {
    if (!tokens?.accessToken) throw this.expired();
    this.accessToken = tokens.accessToken;
    // Server lifetime is ten minutes; refresh early without treating unverified JWT claims as authority.
    this.accessUntil = Date.now() + 9 * 60 * 1000;
  }

  private clear(): void {
    this.generation++;
    this.accessToken = null;
    this.accessUntil = 0;
    this.isAuthenticated$.next(false);
    this.user$.next(null);
    this.profileReady$.next(null);
  }

  private async withLock<T>(work: () => Promise<T>): Promise<T> {
    // Cross-tab serialization is essential: replaying a rotated refresh token revokes the session.
    if (!navigator.locks) return Promise.reject(new Error('browser_auth_lock_unavailable'));
    return await navigator.locks.request('footballer-auth:' + this.base, work);
  }

  private options() { return { headers: this.headers, withCredentials: true }; }
  private expired() { return Object.assign(new Error('login_required'), { status: 401 }); }
}
